import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import delv from 'dlv';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import BonusPoints from 'components/bonus-points';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward/use-redeem-reward';
import ErrorBoundary from 'components/error-boundary';
import Incrementor from 'components/incrementor';
import { brandFont } from 'components/layout/brand-font';
import LegalDisclaimers from 'components/legal-disclaimers';
import NutritionModal from 'components/nutrition';
import { AllergensDisclaimer } from 'components/nutrition/allergens-disclaimer';
import { useNutritionFacts } from 'components/nutrition/use-nutrition-facts';
import { Calories } from 'components/price-and-calories';
import { PricePerUnit } from 'components/product-detail/price-per-unit/price-per-unit';
import { ReadMoreAccordion } from 'components/read-more-accordion';
import BlockRenderer from 'components/sanity-block-renderer';
import SelectionContainer from 'components/selection-container';
import { StickyFooter } from 'components/sticky-footer';
import { TextWithArrowButton } from 'components/text-with-arrow-button';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnce from 'hooks/use-effect-once';
import { useFormatCalories } from 'hooks/use-format-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import { useIsEditCartEnabled } from 'hooks/use-is-edit-cart-enabled';
import useMediaQuery from 'hooks/use-media-query';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { mapEntryTypeToOfferType } from 'pages/cart/utils';
import { useDayPartContext } from 'state/day-part';
import { useErrorContext } from 'state/errors';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyRewardsFeedback } from 'state/loyalty/hooks/use-loyalty-rewards-feedback';
import { isLoyaltyOffer, isSurpriseOffer } from 'state/loyalty/types';
import { useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useMenuUIContext } from 'state/menu-ui';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import stylesConstants from 'styles/constants';
import { primitive } from 'styles/constants/primitives';
import { MIN_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';
import {
  getCartEntryBenefit,
  getIndividualItemPrice,
  getItemLoyaltyRewardDiscount,
} from 'utils/cart/helper';
import { EventName, emitEvent } from 'utils/event-hub';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import {
  buildCalorieMapForPickerPossibilities,
  computeCurrentCalories,
  computeCurrentPrice,
  computeSelectedOption,
  formattedCalorieMapForPickerAspectCalorieMap,
  reducePickerCalorieMapToRanges,
} from 'utils/menu';
import { useIsBrowseMenu } from 'utils/menu/hooks/use-is-browse-menu';
import { ModifierComponentStyle, getDefaultModifiersForMenuItem } from 'utils/menu/modifiers';
import { hasExcessSodium } from 'utils/menu/sodium';
import { goToMenu, routes } from 'utils/routing';
import {
  calculateTotalSteps,
  createPickerPromotionMap,
  getItemModifiers,
  getOptionsFromParams,
  getSelectionsFromMenuData,
  transformMenuObjectToCartItem,
} from 'utils/wizard';
import { getRequirementsErrors } from 'utils/wizard/requirements';

import { ENABLE_AUTO_SCROLL_ON_CONSEQUENT_STEPS, WIZARD_DESCRIPTION_COLOR } from './constants';
import { CtaButton } from './cta-button';
import CustomizationContainer from './customization-container';
import DescriptionTitle from './description-title';
import { useMenuItemStatus } from './hooks/use-menu-item-status';
import ItemUI from './item-ui';
import { OptionTile } from './option-tile';
import ParallelUI from './parallel-ui';
import PriceCaloriesTitle, { Price } from './price-calories-title';
import { modifierSelectionsReducer } from './reducers/modifier-selection';
import Scroller from './scroller';
import SeriesUI from './series-ui';
import SeriesWithLastAsModifierUI from './series-with-last-as-modifier-ui';
import SodiumFlag from './sodium-icon';
import Title from './title';
import { useWizard } from './wizard-provider';
import {
  getChosenPickerAspectOptions,
  getDescriptionText,
  getNewSelections,
  getOfferPrice,
  getSelectionsBasedOnMaxAmount,
  getTitleText,
  getTotalQuantity,
} from './wizard-ui-helpers';

const Information = styled.h3`
  margin: 0 0 0 1rem;
  font: ${brandFont.headerThree};
  color: ${({ theme }) => theme.token('text-default')};
`;

const Description = styled.div`
  color: ${WIZARD_DESCRIPTION_COLOR};
  font-family: ${Styles.fontFamily.body};
  margin: 0;
  margin-bottom: 1rem;
`;

export const fixedButtonCSS = css`
  color: ${p => p.theme.token('text-button-primary')};
  font-size: 1rem;
`;

export const regularButtonCSS = css`
  margin-bottom: ${primitive.$spacing7};
`;

const Wizard = ({
  upsertCart,
  removeFromCart,
  currentCartEntryId,
  getCurrentCartEntry,
  className,
  data,
  loading,
  location,
  navigate,
  priceForItemOptionModifier,
  pricingFunction,
  priceForComboSlotSelection,
  setOfferEntry,
  setSelectedOfferPrice,
  onSelectProduct,
}) => {
  const offerCartEntryIsSelected = useRef(false);
  const { setSanityDataRef } = useErrorContext();
  const { id: currentMenuItemId } = useParams();
  const { search, state: locationState } = location;
  const isDesktop = useMediaQuery('desktop');
  const { formatCurrencyForLocale } = useUIContext();
  const { setActiveCartEntries, activeCartEntries } = useMenuUIContext();
  const { activeDayParts, dayParts } = useDayPartContext();
  const { selectedOffer: selectedLegacyOffer, clearSelectedOffer } = useOffersContext();
  const { isOffer, showStaticMenu, setSelectedStaticMenuItem } = useMenuContext();
  useLoyaltyRewardsFeedback(activeCartEntries);
  const { getAvailableRewardFromCartEntry, loyaltyEnabled, loyaltyUser } = useLoyaltyContext();
  const {
    selectFavorite,
    currentCartEntry: currentFavoriteEntry,
    tempFavorite: currentFavorite,
  } = useFavoritesContext();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const selectedOffer = selectedLoyaltyOffer || selectedLegacyOffer;
  const displayNutritionWithModifiersFromSanity = useFlag(
    LaunchDarklyFlag.DISPLAY_NUTRITION_WITH_MODIFIERS_FROM_SANITY
  );
  const enableAllergensDisclaimer = useFlag(LaunchDarklyFlag.ENABLE_ALLERGENS_DISCLAIMER);
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const enableCaloriesCalculator = useFlag(LaunchDarklyFlag.ENABLE_CALORIES_CALCULATOR);
  const enableFixedAddToOrderAndQuantityButton = useFlag(
    LaunchDarklyFlag.ENABLE_FIXED_ADD_TO_ORDER_AND_QUANTITY_BUTTON
  );

  const mParticle = useMParticleContext();
  const { formatMessage } = useIntl();
  const formatCalories = useFormatCalories();
  const formatJoules = useFormatJoules();
  const [pickerSelections, setPickerSelections] = useState({});
  const [modifierSelections, modifierDispatch] = useReducer(modifierSelectionsReducer, []);
  const [modifierFifoCounter, setModifierFifoCounter] = useState(0);
  const [comboSlotSelections, setComboSlotSelections] = useState({});

  // Track the modification of item, combo slot and picker aspect.
  // These values are initially set to false and is set to true if the user
  // changes these values away from the initial state.
  const addToCartEventAttrs = useRef({
    itemModified: false,
    comboSlotSelection: false,
    pickerAspectSelection: false,
  });

  const [quantity, setQuantity] = useState(1);
  const { incentiveNotInMenu, limitPerOrderMet, rewardPointCost } = useRedeemReward({
    ...data,
    quantity,
  });

  const [cta, setCta] = useState({
    children: [],
    onClick: () => {},
  });
  const [shouldConfirmChange, setShouldConfirmChange] = useState(false);

  const [EditingPickerAspectChangeModal, openEditingPickerAspectChangeModal] = useDialogModal({
    onConfirm: ({ pickerAspectKey, aspectIdentifier }) => {
      handlePickerSelectionNoConfirmation(pickerAspectKey, aspectIdentifier);
    },
    showCancel: true,
    modalAppearanceEventMessage: 'Remove customizations',
  });

  const {
    state: { currentStep, isFinalStep },
    actions: { setTotalSteps },
  } = useWizard();

  const isParallelUI = data.uiPattern === 'parallel';
  const pickerAspectsCount = data.pickerAspects?.length ?? 0;
  const { isEdit, isFavorite } = getOptionsFromParams(search);
  const rewardRedemptionBenefitIdParam = useGetUrlSearchParam('reward');
  const enableEditCart = useIsEditCartEnabled({ cartHasIncentive: isOffer });
  const enableFavorites = useFlag(LaunchDarklyFlag.ENABLE_FAVORITES);
  const isEditing = useMemo(() => {
    // We are only editing favorites if isFavorite flag and currently editing a favorite
    // If its a favorite only return true if favorites is enabled
    if (isFavorite && currentFavorite) {
      return enableFavorites;
    }

    return enableEditCart ? isEdit && currentCartEntryId : false;
  }, [isFavorite, currentFavorite, enableEditCart, isEdit, currentCartEntryId, enableFavorites]);
  const selectionsEntry = useMemo(() => {
    if (!isEditing) {
      return undefined;
    }

    return currentFavorite ? currentFavoriteEntry : getCurrentCartEntry(isOffer);
  }, [currentFavoriteEntry, getCurrentCartEntry, isEditing, currentFavorite, isOffer]);
  const dispatch = useAppDispatch();
  const { cartId: cartIdEditing = '' } = selectionsEntry ?? {};

  useEffect(() => {
    setSanityDataRef({ data });
  }, [data, setSanityDataRef]);

  const setModifiers = useCallback((selections, reset = false) => {
    if (reset) {
      // Anytime you input derived selections using `getSelectionsFromMenuData` it will make sure every selections is a valid option for the current comboSlot/item
      // We can just clear out the current state and use the derived state instead
      modifierDispatch({
        type: 'CLEAR_ALL_SELECTIONS',
      });
    }
    (selections ?? []).forEach(selection => {
      setModifierFifoCounter(prevCount => {
        modifierDispatch({
          ...selection,
          fifoCounter: prevCount,
          type: 'DEFAULT_SELECTION',
        });

        return prevCount + selection.option.maxAmount;
      });
    });
  }, []);

  // Resets modifiers to defaults, will try to keep any valid selections you have
  const setDefaultModifierSelections = useCallback(
    (item, comboSlotId) => {
      // When resetting back to defaults we need to make sure our current modifier selections are valid for the new item/comboSlot
      modifierDispatch({
        type: 'CLEAR_INAPPLICABLE_SELECTIONS',
        item,
        comboSlotId,
      });
      setModifiers(getItemModifiers(item, [], comboSlotId));
    },
    [setModifiers]
  );

  const setDefaultSelections = useCallback(
    ({ useUserSelections = false, pickerSelectionOverrides }) => {
      const derivedState = getSelectionsFromMenuData({
        data,
        ...(useUserSelections && selectionsEntry && { selectionsEntry }),
        ...(pickerSelectionOverrides && { pickerSelectionOverrides }),
      });

      if (onSelectProduct) {
        onSelectProduct(computeSelectedOption(derivedState.pickerSelections, data));
      }

      // Always reset these if the getSelectionsFromMenuData returns undefined
      if (useUserSelections) {
        setQuantity(derivedState.quantity ?? 1);
      }
      setPickerSelections(derivedState.pickerSelections ?? {});
      setComboSlotSelections(derivedState.comboSlotSelections ?? {});
      setModifiers(derivedState.modifierSelections ?? [], true);
    },
    [data, onSelectProduct, selectionsEntry, setModifiers]
  );

  // Anytime data changes or the cartEntry we are editing swaps out we need to reset everything
  // NOTE: for cart preview on desktop you can change the item you are editing while you have wizard open
  useEffect(() => {
    // reset confirmation anytime the thing we are customizing changes
    setShouldConfirmChange(true);
    // Get the derived state using the users selections if any
    setDefaultSelections({ useUserSelections: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMenuItemId, currentCartEntryId]);

  // Recalculate the total number of steps any time the pickerSelection changes
  useEffect(() => {
    if (!loading && data) {
      setTotalSteps(calculateTotalSteps(data, pickerSelections));
    }
  }, [data, loading, pickerSelections, setTotalSteps]);

  const handlePickerSelectionNoConfirmation = useCallback(
    (pickerAspectKey, aspectIdentifier) => {
      setPickerSelections(currentPickerSelections => {
        const newPickerSelections = {
          ...currentPickerSelections,
          [pickerAspectKey]: aspectIdentifier,
        };
        setDefaultSelections({ pickerSelectionOverrides: newPickerSelections });
        return newPickerSelections;
      });
      // Make sure we don't show the confirmation again until something resets our components state
      setShouldConfirmChange(false);
    },
    [setDefaultSelections]
  );

  const handlePickerSelection = useCallback(
    (pickerAspectKey, aspectIdentifier) => {
      if (pickerSelections[pickerAspectKey] !== aspectIdentifier) {
        addToCartEventAttrs.current.pickerAspectSelection = true;
      }

      // if we are editing and we haven't reset the selections yet show confirmation modal
      if (selectionsEntry && shouldConfirmChange) {
        openEditingPickerAspectChangeModal({ pickerAspectKey, aspectIdentifier });
      } else {
        handlePickerSelectionNoConfirmation(pickerAspectKey, aspectIdentifier);
      }
    },
    [
      handlePickerSelectionNoConfirmation,
      openEditingPickerAspectChangeModal,
      pickerSelections,
      selectionsEntry,
      shouldConfirmChange,
    ]
  );

  const handleComboSelection = useCallback(
    (comboSlotId, newSelection, selectionCount, respectMaximum) => {
      const comboSlot = comboSlotSelections[comboSlotId];
      const { max } = selectionCount;
      if (!comboSlot) {
        return;
      }

      const { data: slotData, selections } = comboSlot;
      const isAlreadySelected = selections.map(sel => sel.option).includes(newSelection);
      let newSelections;
      if (isAlreadySelected) {
        // remove selection
        newSelections = selections.filter(selection => selection.option !== newSelection);
        modifierDispatch({ type: 'CLEAR_COMBO_SLOT_SELECTIONS', comboSlotId });
      } else {
        if (respectMaximum) {
          const totalQuantity = getTotalQuantity(selections);
          if (totalQuantity >= max && selections[0]?.quantity !== 0) {
            selections[0].quantity -= 1;
          }
          if (selections.length > max || selections[0]?.quantity === 0) {
            // remove selection
            selections.shift();
            // clear modifiers for combo slot
            modifierDispatch({ type: 'CLEAR_COMBO_SLOT_SELECTIONS', comboSlotId });
          }
          newSelections = [...selections, { option: newSelection, quantity: 1 }];
          setDefaultModifierSelections(newSelection.option, comboSlotId);
        } else {
          newSelections = [...selections, { option: newSelection, quantity: 1 }];
          setDefaultModifierSelections(newSelection.option, comboSlotId);
        }
      }

      addToCartEventAttrs.current.comboSlotSelection = true;
      setComboSlotSelections(prevState => ({
        ...prevState,
        [comboSlotId]: { data: slotData, selections: newSelections },
      }));
    },
    [comboSlotSelections, setDefaultModifierSelections]
  );

  const handleComboSlotSelectionQuantityUpdate = useCallback(
    (comboSlotId, selectionToUpdate, selectionQuantity) => {
      const comboSlot = comboSlotSelections[comboSlotId];
      if (!comboSlot) {
        return;
      }
      const { data: slotData, selections } = comboSlot;
      const slotMaxAmount = slotData?.maxAmount || 0;
      const newSelections = slotData?.respectMaximum
        ? getSelectionsBasedOnMaxAmount(
            selections,
            selectionToUpdate,
            slotMaxAmount,
            selectionQuantity
          )
        : getNewSelections(selections, selectionToUpdate, selectionQuantity);

      addToCartEventAttrs.current.comboSlotSelection = true;
      setComboSlotSelections(prevState => ({
        ...prevState,
        [comboSlotId]: { data: slotData, selections: newSelections },
      }));
    },
    [comboSlotSelections]
  );

  const handleModifierSelection = useCallback(
    (itemId, modifier, itemOption, comboSlotId) => {
      addToCartEventAttrs.current.itemModified = true;
      // boolean componentStyles let you maintain an on/off selection of an itemOption.options.
      // there is an expected heuristic that option[0] === off and option[1] === on.
      if (itemOption.componentStyle === ModifierComponentStyle.Boolean) {
        modifierDispatch({
          comboSlotId,
          itemId,
          option: itemOption,
          type: 'TOGGLE_BOOLEAN_SELECTION',
        });
      }
      // stepper componentStyles let you maintain one selection of many options by moving along
      // the index of the options.
      else if (itemOption.componentStyle === ModifierComponentStyle.Stepper) {
        modifierDispatch({
          comboSlotId,
          itemId,
          modifier,
          option: itemOption,
          type: 'MOVE_STEPPER_SELECTION',
        });
      }
      // selector componentStyles let you add and remove child options based on min/maxAmount
      // logic, and fifo (first in, first out) counters. 'selector' is the default componentStyle
      // when it is left empty in sanity.
      else if (
        !itemOption.componentStyle ||
        itemOption.componentStyle === ModifierComponentStyle.Selector
      ) {
        const isModifierSelected = !!modifierSelections.find(
          selection =>
            selection.itemId === itemId &&
            selection._key === itemOption._key &&
            selection.modifier._key === modifier._key &&
            selection.comboSlotId === comboSlotId
        );

        modifierDispatch({
          comboSlotId,
          fifoCounter: !isModifierSelected && modifierFifoCounter,
          itemId,
          modifier,
          option: itemOption,
          type: isModifierSelected ? 'REMOVE_SELECTION' : 'ADD_SELECTION',
        });

        if (!isModifierSelected) {
          setModifierFifoCounter(prevState => prevState + 1);
        }
      }
    },
    [modifierSelections, modifierFifoCounter]
  );

  const handleModifierQuantityUpdate = useCallback(
    (newQuantity, itemOptionKey, itemId, key, oldQuantity, maxAmount, comboSlotId) => {
      const isIncrease = newQuantity > oldQuantity;
      addToCartEventAttrs.current.itemModified = true;
      modifierDispatch({
        comboSlotId,
        fifoCounter: modifierFifoCounter,
        itemId,
        modifier: { _key: key },
        option: { _key: itemOptionKey, maxAmount },
        quantity: newQuantity,
        type: isIncrease ? 'ADD_QUANTITY' : 'REMOVE_QUANTITY',
      });
      // only increment on adds, we don't care when they remove
      if (isIncrease) {
        setModifierFifoCounter(prevState => prevState + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modifierSelections, modifierFifoCounter]
  );

  // This keeps activeCartEntries up to date with the latest pending changes.
  // activeCartEntries is a pending state to know what the current selections will output
  useEffect(() => {
    try {
      if (!loading && data) {
        const selectedOption = computeSelectedOption(pickerSelections, data);
        // We were unable to calculate the selectedOption, most likely because there are no options available to order
        if (!selectedOption) {
          return;
        }

        const { mainEntry, upsellEntries } = transformMenuObjectToCartItem(
          selectedOption,
          priceForItemOptionModifier,
          pricingFunction,
          priceForComboSlotSelection,
          {
            quantity,
            modifierSelections,
            pickerSelections,
            comboSlotSelections,
            location,
            quickConfigs: data?.quickConfigs,
            existingCartId: selectionsEntry?.cartId,
            shouldLogEvents: isOffer,
            mParticle,
          }
        );

        setActiveCartEntries([mainEntry, ...upsellEntries]);
      }
    } catch (error) {
      // No need to handle this error
    }
  }, [
    setActiveCartEntries,
    data,
    loading,
    comboSlotSelections,
    pickerSelections,
    modifierSelections,
    selectionsEntry,
    priceForItemOptionModifier,
    pricingFunction,
    quantity,
    isOffer,
    mParticle,
    location,
    priceForComboSlotSelection,
  ]);

  const discountPrice = () => {
    const cartEntry = getCartEntryBenefit(
      activeCartEntries,
      getAvailableRewardFromCartEntry,
      rewardRedemptionBenefitIdParam
    );

    if (!cartEntry) {
      return 0;
    }

    let discount = 0;
    // from loyalty redemption
    if (rewardRedemptionBenefitIdParam) {
      discount = incentiveNotInMenu ? cartEntry.price : getIndividualItemPrice({ cartEntry });
    } else if (cartIdEditing) {
      discount = getItemLoyaltyRewardDiscount({ cartEntry, appliedLoyaltyRewards });
    }

    return discount;
  };

  // If a loyalty incentive (offer, reward) is not available as a regular menu item,
  // the incrementor also increments incentive redemptions in addition to adjusting quantity.
  // This takes into account user point and determines whether there are
  // sufficient points for further redemptions.
  const isIncrementDisabledForRewardRedemption = () => {
    if (limitPerOrderMet) {
      return true;
    }

    if (!incentiveNotInMenu || !loyaltyEnabled) {
      return false;
    }

    const checkRedemptionEligibility = () => {
      if (rewardRedemptionBenefitIdParam) {
        const incrementedPointCost = rewardPointCost * (quantity + 1);
        return stagedCartPoints <= incrementedPointCost;
      }
    };

    return cartIdEditing || checkRedemptionEligibility();
  };

  const addToOrder = useCallback(() => {
    const { mainEntry, upsellEntries, modifiersInjected } = transformMenuObjectToCartItem(
      computeSelectedOption(pickerSelections, data),
      priceForItemOptionModifier,
      pricingFunction,
      priceForComboSlotSelection,
      {
        quantity,
        modifierSelections,
        pickerSelections,
        comboSlotSelections,
        location,
        quickConfigs: data?.quickConfigs,
        existingCartId: selectionsEntry?.cartId,
        shouldLogEvents: isOffer,
        mParticle,
      }
    );
    // Don't include the main entry if its an offer that gets set separately in offerCtx
    const cartEntries = isOffer ? [...upsellEntries] : [mainEntry, ...upsellEntries];

    // If we are currently editing a favorite don't continue
    if (currentFavorite) {
      return selectFavorite({ cartEntries });
    }

    // Only update the cart if we actually have anything to add. (Offers will most likely not have anything here)
    if (cartEntries.length) {
      upsertCart(cartEntries, addToCartEventAttrs.current);
    }

    if (rewardRedemptionBenefitIdParam) {
      const entryRedemption = getCartEntryBenefit(
        cartEntries,
        getAvailableRewardFromCartEntry,
        rewardRedemptionBenefitIdParam
      );
      if (entryRedemption) {
        dispatch(
          actions.loyalty.applyReward({
            rewardBenefitId: rewardRedemptionBenefitIdParam,
            cartId: entryRedemption.cartId,
            // If a loyalty incentive is not available as a regular menu entry,
            // the quantity must match rewards applied
            ...(incentiveNotInMenu && { quantity: entryRedemption.quantity }),
          })
        );
      }
    }

    if (isOffer && selectedOffer) {
      offerCartEntryIsSelected.current = true;
      const marketPrice = pricingFunction(
        {
          vendorConfigs: delv(selectedOffer, 'marketPrice.vendorConfigs', {}),
        },
        quantity,
        modifiersInjected
      );
      const restaurantPosPrice = pricingFunction(
        {
          vendorConfigs: delv(selectedOffer, 'vendorConfigs', {}),
        },
        quantity,
        modifiersInjected
      );
      const selectedOfferPrice = getOfferPrice({
        marketPrice,
        offerPrice: selectedOffer && selectedOffer.offerPrice,
        restaurantPosPrice,
      });
      if (isLoyaltyOffer(selectedOffer)) {
        if (!selectedOffer.isStackable) {
          appliedOffers.forEach(offer => {
            if (!offer.isStackable) {
              removeFromCart({ cartId: offer.cartId });
            }
          });
        }

        dispatch(
          actions.loyalty.applyOffer({
            id: selectedOffer.loyaltyEngineId,
            type: selectedOffer.type,
            cartId: mainEntry.cartId,
            isStackable: selectedOffer.isStackable,
            isSurprise: isSurpriseOffer(selectedOffer),
            cmsId: selectedOffer._id,
          })
        );
        dispatch(actions.loyalty.setSelectedOffer(null));
        upsertCart([
          {
            ...mainEntry,
            price: selectedOfferPrice,
            offerVendorConfigs: selectedOffer.vendorConfigs,
            ...(mainEntry && { type: mapEntryTypeToOfferType(mainEntry.type) }),
          },
          ...upsellEntries,
        ]);
      } else {
        setOfferEntry({ ...mainEntry, sanityId: selectedOffer._id });
        setSelectedOfferPrice(selectedOfferPrice);
      }
    }

    hapticImpact({ style: HapticsImpactStyle.Heavy });

    // if editing and user coming from the cart or if is coming from the surprise modal
    if ((selectionsEntry && locationState?.isFromCart) || locationState?.isFromSurprise) {
      return navigate(routes.cart);
    }

    goToMenu({ navigate });
  }, [
    pickerSelections,
    data,
    priceForItemOptionModifier,
    pricingFunction,
    priceForComboSlotSelection,
    quantity,
    modifierSelections,
    comboSlotSelections,
    location,
    selectionsEntry,
    isOffer,
    mParticle,
    currentFavorite,
    rewardRedemptionBenefitIdParam,
    selectedOffer,
    locationState,
    navigate,
    selectFavorite,
    upsertCart,
    getAvailableRewardFromCartEntry,
    dispatch,
    incentiveNotInMenu,
    appliedOffers,
    removeFromCart,
    setOfferEntry,
    setSelectedOfferPrice,
  ]);

  const setModalCta = useCallback(
    ({ children, onClick }) =>
      setCta({
        children,
        onClick,
      }),
    []
  );

  const handleRootQuantityUpdate = value => {
    const rewardApplied = appliedLoyaltyRewards[cartIdEditing];
    if (value < quantity && rewardApplied?.timesApplied > 1) {
      dispatch(
        actions.loyalty.unApplyReward({
          rewardBenefitId: rewardApplied.rewardId,
          cartId: cartIdEditing,
          loyaltyUser,
        })
      );
    }
    setQuantity(value);
  };

  // While editing in the cart preview while wizard is open we end up in a single render where there are pickerSelections but no pickerAspects in the data
  // Avoid reducing over the pickerSelections if the data has no pickerAspects
  const excludesForPickerAspects = data?.pickerAspects
    ? Object.keys(pickerSelections).reduce((acc, selection) => {
        const { pickerAspectItemOptionMappings, pickerAspects } = data;
        const pickerAspect = pickerAspects.find(({ _id }) => _id === selection);

        if (!pickerAspect) {
          return acc;
        }

        const itemOptionMappings = (pickerAspectItemOptionMappings || []).find(
          ({ pickerAspect: { _id } }) => _id === pickerAspect._id
        );

        if (!itemOptionMappings) {
          return acc;
        }

        return acc.concat(itemOptionMappings.options || []);
      }, [])
    : [];

  const calorieMap = useMemo(
    () => buildCalorieMapForPickerPossibilities(data, displayNutritionWithModifiersFromSanity),
    [data, displayNutritionWithModifiersFromSanity]
  );

  const calorieMapForCurrentPickerStep = useMemo(
    () =>
      data.uiPattern !== 'parallel' &&
      Object.values(pickerSelections)
        .slice(0, currentStep)
        .reduce((map, identifier) => (map ? map[identifier] : null), calorieMap),
    [calorieMap, currentStep, data.uiPattern, pickerSelections]
  );

  const calorieRangesForCurrentPickerAspect = useMemo(
    () =>
      // while the user still has picker selections to make, reduce
      // the remaining choices to a possible range of calories.
      // the range is no longer used once all selections have been made
      !isParallelUI && currentStep < pickerAspectsCount
        ? reducePickerCalorieMapToRanges(calorieMapForCurrentPickerStep)
        : null,
    [calorieMapForCurrentPickerStep, currentStep, isParallelUI, pickerAspectsCount]
  );

  const pickerSelectionSteps = Object.keys(pickerSelections).length;
  // `currentStep` starts at 0
  const isLastPickerSelectionStep =
    !!pickerSelectionSteps && pickerSelectionSteps <= currentStep + 1;
  const pickerPromotionMap = isLastPickerSelectionStep
    ? createPickerPromotionMap(pickerSelectionSteps - 1, pickerSelections, data.options)
    : {};

  const selectedPicker = computeSelectedOption(pickerSelections, data);

  const renderDisplay = useCallback(
    (info, presentPrice) => {
      const addToOrderText = formatMessage({
        id: selectionsEntry ? 'updateCart' : 'addToCart',
      });

      if (info._type === 'picker' || info._type === 'combo') {
        const pricePickerAspect = (pickerAspectId, pickerAspectOptionId) => {
          const pickerSelectionsWithAspect = {
            ...pickerSelections,
            [pickerAspectId]: pickerAspectOptionId,
          };
          const selection = computeSelectedOption(pickerSelectionsWithAspect, info);

          const defaultModifiersForSelection = getDefaultModifiersForMenuItem(selection);

          const [totalForBase] = computeCurrentPrice({
            comboSlotSelections,
            item: info,
            pickerSelections: pickerSelectionsWithAspect,
            modifierSelections: defaultModifiersForSelection,
            pricingFunction,
            quantity: 1,
          });

          return formatCurrencyForLocale(totalForBase);
        };

        const UIComponent =
          {
            parallel: ParallelUI,
            series: SeriesUI,
          }[info.uiPattern] || SeriesWithLastAsModifierUI;

        const calorieMapForUiPattern =
          info.uiPattern === 'parallel'
            ? calorieMap
            : formattedCalorieMapForPickerAspectCalorieMap(
                calorieRangesForCurrentPickerAspect,
                formatCalories
              );

        return (
          <ErrorBoundary>
            <UIComponent
              addToOrder={addToOrder}
              addToOrderText={addToOrderText}
              calorieMap={calorieMapForUiPattern}
              comboSlotSelections={comboSlotSelections}
              currentPrice={presentPrice}
              pricePickerAspect={pricePickerAspect}
              excludesForPickerAspects={excludesForPickerAspects}
              handleComboSelection={handleComboSelection}
              handleComboSlotSelectionQuantityUpdate={handleComboSlotSelectionQuantityUpdate}
              handleModifierQuantityUpdate={handleModifierQuantityUpdate}
              handleModifierSelection={handleModifierSelection}
              handlePickerSelection={handlePickerSelection}
              modifierSelections={modifierSelections}
              pickerAspects={info.pickerAspects || []}
              pickerOptions={info.options}
              pickerAspectPromotionMap={pickerPromotionMap}
              pickerSelection={selectedPicker}
              pricingFunction={pricingFunction}
              quickConfigs={info.quickConfigs || []}
              selectedPickerIdentifiers={pickerSelections}
              setModalCta={setModalCta}
            />
          </ErrorBoundary>
        );
      } else if (info._type === 'item') {
        return (
          <ErrorBoundary>
            {!showStaticMenu && (
              <ItemUI
                addToOrder={addToOrder}
                addToOrderText={addToOrderText}
                currentPrice={presentPrice}
                excludesForPickerAspects={excludesForPickerAspects}
                handleModifierQuantityUpdate={handleModifierQuantityUpdate}
                handleModifierSelection={handleModifierSelection}
                item={info}
                modifierSelections={modifierSelections}
                setModalCta={setModalCta}
              />
            )}
          </ErrorBoundary>
        );
      }
    },
    [
      formatMessage,
      selectionsEntry,
      calorieMap,
      calorieRangesForCurrentPickerAspect,
      formatCalories,
      addToOrder,
      comboSlotSelections,
      excludesForPickerAspects,
      handleComboSelection,
      handleComboSlotSelectionQuantityUpdate,
      handleModifierQuantityUpdate,
      handleModifierSelection,
      handlePickerSelection,
      modifierSelections,
      pickerPromotionMap,
      selectedPicker,
      pricingFunction,
      pickerSelections,
      setModalCta,
      formatCurrencyForLocale,
      showStaticMenu,
    ]
  );

  const renderBonusPointsDescription = () => {
    if (currentStep < pickerAspectsCount) {
      return null;
    }

    let promotion;
    for (const pickerSelectionId in pickerSelections) {
      const pickerIdentifier = pickerSelections[pickerSelectionId];
      promotion = pickerPromotionMap[`${pickerSelectionId}-${pickerIdentifier}`];

      if (promotion) {
        break;
      }
    }

    return promotion?.bonusPoints && <BonusPoints bonusPoints={promotion?.bonusPoints} />;
  };

  const [baseTotal, extraTotal] = computeCurrentPrice({
    comboSlotSelections,
    item: data,
    modifierSelections,
    pickerSelections,
    pricingFunction,
    isOffer,
    selectedOffer,
    getOfferPrice,
    quantity,
  });

  const totalPrice = baseTotal + extraTotal;
  const totalWithDiscount = incentiveNotInMenu ? 0 : Math.max(totalPrice - discountPrice(), 0);
  const currentPrice = formatCurrencyForLocale(totalWithDiscount);
  // If price is not set correctly, don't allow adding such an item to cart, except offers
  const isCurrentPriceValid = totalPrice > 0 || isOffer;

  const isOverSodiumLimit = hasExcessSodium({
    selectedOpt: computeSelectedOption(pickerSelections, data),
    comboSlotSelections,
    modifierSelections,
  });

  const chosenPickerAspectOptions = getChosenPickerAspectOptions({
    pickerAspects: data.pickerAspects,
    pickerSelections,
    step: currentStep,
    uiPattern: data.uiPattern,
  });
  const titleText = getTitleText(data?.name?.locale, chosenPickerAspectOptions);

  const itemDayParts =
    data?.operationalItem?.daypart || // dayparts on an individual item
    data?.options?.find(pickable => pickable.option?.operationalItem?.daypart)?.option
      ?.operationalItem?.daypart || // dayparts on an item picker
    data?.options?.find(pickable => pickable.option?.mainItem)?.option?.mainItem?.operationalItem
      ?.daypart || // dayparts on the main item of a combo
    [];

  const isItemAvailableForDaypart =
    !itemDayParts.length || // item not associated with any daypart
    !dayParts.length || // brand has no dayparts
    activeDayParts.some(({ key: dayPart }) => itemDayParts.includes(dayPart));

  const currentUrl = useLocation().pathname;

  const { isBrowseMenu } = useIsBrowseMenu(currentUrl);
  const { isStoreOpenAndAvailable } = useStoreContext();

  const handleFindRestaurantClick = () => {
    const { _type, _id, name } = data || {};
    setSelectedStaticMenuItem({
      itemId: `${_type}-${_id}`,
      itemName: name?.locale,
      search,
    });
    return isBrowseMenu && isStoreOpenAndAvailable ? goToMenu() : navigate(routes.storeLocator);
  };

  const descriptionText = getDescriptionText(chosenPickerAspectOptions);

  const pickerSelection = computeSelectedOption(pickerSelections, data);

  const nutritionFacts = useNutritionFacts({
    pickerSelection,
    comboSlotSelections,
    itemName: titleText,
    itemImage: data?.image,
    displayNutritionWithModifiersFromSanity,
  });

  const nutritionWithModifiers =
    pickerSelection?.mainItem?.nutritionWithModifiers ||
    delv(pickerSelection || data, 'nutritionWithModifiers') ||
    {};

  const currentCalories = displayNutritionWithModifiersFromSanity
    ? nutritionWithModifiers.calories || 0
    : computeCurrentCalories({
        comboSlotSelections,
        item: data,
        modifierSelections,
        pickerSelections,
        quantity,
      });

  const formattedCalories = formatCalories(currentCalories);
  const formattedJoules = formatJoules(currentCalories);
  const euFormattedNutritionText = formatMessage(
    { id: 'euNutritionValues' },
    {
      calories: formattedCalories,
      joules: formattedJoules,
    }
  );

  const calories = enableEUNutritionDisplay ? euFormattedNutritionText : formattedCalories;

  useEffect(() => {
    if (!calories) {
      return;
    }
    // Emit event for when calories are loaded since menu performance tracking uses this.
    emitEvent(EventName.MENU_ITEM_CALORIES_LOADED);
  }, [calories]);

  const hideCalories = !!data.hideCalories;

  const { cartEntries } = useOrderContext() ?? {};
  const { availableCartQuantity, maxCartQuantityMet: isLimitReached } = getMenuObjectCartQuantity({
    menuObject: pickerSelection || data,
    quantityToAdd: quantity,
    cartEntries,
    cartIdEditing,
  });

  const maxCartQuantityMet = isLimitReached && !isEditing;
  const disableIncrementQuantity = maxCartQuantityMet || isIncrementDisabledForRewardRedemption();
  const disableIncrementor =
    maxCartQuantityMet || (loyaltyEnabled && incentiveNotInMenu && cartIdEditing);

  // When changing pickerSelection we need to cap the quantity to the current pick max quantity
  if (!maxCartQuantityMet && availableCartQuantity < quantity) {
    handleRootQuantityUpdate(availableCartQuantity);
  }

  const itemStatus = useMenuItemStatus({
    showStaticMenu,
    enableOrdering,
    isItemAvailableForDaypart,
    maxCartQuantityMet,
  });

  const showActionOrMessageInBottom = !isDesktop && enableFixedAddToOrderAndQuantityButton;
  const buttonCSS = showActionOrMessageInBottom ? fixedButtonCSS : regularButtonCSS;

  const showIncrementor =
    !showStaticMenu && !isOffer && !maxCartQuantityMet && isItemAvailableForDaypart;
  const incrementor = (
    <>
      <Incrementor
        aria-label={`Quantity ${quantity} of ${titleText}`}
        name={titleText}
        max={availableCartQuantity}
        min={MIN_CART_QUANTITY}
        onChange={handleRootQuantityUpdate}
        value={quantity}
        disableIncrement={disableIncrementQuantity}
        disabled={disableIncrementor}
      >
        {!showActionOrMessageInBottom && currentPrice}
      </Incrementor>
      <VisuallyHidden role="alert">
        {formatMessage(
          { id: 'quantityUpdatedAlert' },
          {
            quantity: quantity.toString(),
            itemName: data?.name?.locale ?? '',
          }
        )}
      </VisuallyHidden>
    </>
  );

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isDesktop && ENABLE_AUTO_SCROLL_ON_CONSEQUENT_STEPS && currentStep > 0) {
      if (scrollRef.current) {
        // scrollIntoView() seems unsupported by Cordova
        const offset = parseInt(stylesConstants.layout.navHeight.mobile, 10) || 0;
        window.scrollTo({
          top: scrollRef.current.getBoundingClientRect().top - offset,
          behavior: 'smooth',
        });
      }
    }
  }, [scrollRef, currentStep, isDesktop]);

  useEffect(() => {
    return () => {
      // Check if the offer option was redeemed before exit the wizard.
      if (isOffer && !offerCartEntryIsSelected.current) {
        clearSelectedOffer();
      }
    };
    // This effect doesn't need any dependency that can cause side effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Scroller className={className}>
        <CustomizationContainer>
          <DescriptionTitle tabIndex="0">
            <Title data-testid="wizard-name" id="modal-heading">
              {titleText}
              {isOverSodiumLimit && <SodiumFlag />}
            </Title>
            <PriceCaloriesTitle>
              <>
                {!showStaticMenu && <Price data-testid="wizard-price">{currentPrice}</Price>}
                {enableCaloriesCalculator && (
                  <Calories hideCalories={hideCalories}>
                    <span data-testid="wizard-calories">
                      {!showStaticMenu && <span> • </span>}
                      {calories}
                    </span>
                  </Calories>
                )}
                {pickerSelection && (
                  <PricePerUnit
                    product={pickerSelection}
                    totalAmount={totalWithDiscount}
                    formattedPrice={currentPrice}
                  />
                )}
              </>
            </PriceCaloriesTitle>
          </DescriptionTitle>

          <ReadMoreAccordion>
            <Description data-testid="wizard-description">
              <BlockRenderer content={data?.description?.localeRaw} />
              {descriptionText}
            </Description>
          </ReadMoreAccordion>

          {renderBonusPointsDescription()}
        </CustomizationContainer>

        <div ref={scrollRef} />

        <CustomizationContainer>
          <EditingPickerAspectChangeModal
            data-testid="picker-aspect-confirmation"
            heading={formatMessage({ id: 'makeThisChange' })}
            buttonLabel={formatMessage({ id: 'continue' })}
            body={formatMessage({ id: 'removeCustomizations' }, { itemType: 'meal' })}
          />

          {renderDisplay(data, currentPrice)}

          <VisuallyHidden> The next step will be to customize your {titleText}</VisuallyHidden>

          {showIncrementor && !showActionOrMessageInBottom && (
            <SelectionContainer required={false} title={formatMessage({ id: 'selectQuantity' })}>
              <OptionTile>{incrementor}</OptionTile>
            </SelectionContainer>
          )}

          {!showActionOrMessageInBottom && (
            <CtaButton
              buttonCSS={buttonCSS}
              cta={cta}
              currentPrice={currentPrice}
              dayParts={dayParts}
              handleFindRestaurantClick={handleFindRestaurantClick}
              isCurrentPriceValid={isCurrentPriceValid}
              isEditing={isEditing}
              isFinalStep={isFinalStep}
              itemDayParts={itemDayParts}
              itemStatus={itemStatus}
              quantity={quantity}
              requirementsErrors={getRequirementsErrors(
                selectedPicker,
                modifierSelections,
                comboSlotSelections
              )}
              showActionOrMessageInBottom={showActionOrMessageInBottom}
              titleText={titleText}
            />
          )}

          <section>
            {!data.hideNutritionLegalDisclaimer && (
              <Information>{formatMessage({ id: 'information' })}</Information>
            )}

            {enableAllergensDisclaimer && (
              <AllergensDisclaimer allergenGroups={nutritionFacts?.[0]?.allergenGroups} />
            )}

            <LegalDisclaimers hideNutritionLegalDisclaimer={data.hideNutritionLegalDisclaimer} />

            {!hideCalories && (
              <NutritionModal
                nutritionFacts={nutritionFacts}
                activatingElement={<TextWithArrowButton messageId="nutritionalInformation" />}
              />
            )}
          </section>
        </CustomizationContainer>
      </Scroller>

      {showActionOrMessageInBottom && (
        <StickyFooter
          padding="16px"
          elements={[
            ...(showIncrementor
              ? {
                  element: incrementor,
                }
              : []),
            {
              element: (
                <CtaButton
                  buttonCSS={buttonCSS}
                  cta={cta}
                  currentPrice={currentPrice}
                  dayParts={dayParts}
                  handleFindRestaurantClick={handleFindRestaurantClick}
                  isCurrentPriceValid={isCurrentPriceValid}
                  isEditing={isEditing}
                  isFinalStep={isFinalStep}
                  itemDayParts={itemDayParts}
                  itemStatus={itemStatus}
                  quantity={quantity}
                  requirementsErrors={getRequirementsErrors(
                    selectedPicker,
                    modifierSelections,
                    comboSlotSelections
                  )}
                  showActionOrMessageInBottom={showActionOrMessageInBottom}
                  titleText={titleText}
                />
              ),
              spaces: showIncrementor ? 2 : 1,
            },
          ]}
        />
      )}
    </>
  );
};

export default props => {
  const { upsertCart, getCurrentCartEntry, cartIdEditing, removeFromCart, cartEntries } =
    useOrderContext();
  const locationContext = useLocationContext();
  const navigate = useNavigate();
  const offers = useOffersContext();
  const currentUrl = useLocation().pathname;
  const { priceForItemOptionModifier, priceForComboSlotSelection, pricingFunction } =
    useMenuContext(currentUrl);
  const { SwitchCartMethodDialog, shouldShowSwitchToMobileDialog, openSwitchCartMethod } =
    useSwitchCartMethodDialog();

  useEffectOnce(() => {
    if (shouldShowSwitchToMobileDialog) {
      openSwitchCartMethod({ onCancel: () => navigate(routes.menu) });
    }
  });

  return (
    <>
      <Wizard
        priceForItemOptionModifier={priceForItemOptionModifier}
        pricingFunction={pricingFunction}
        priceForComboSlotSelection={priceForComboSlotSelection}
        setOfferEntry={offer => offers.setSelectedOfferCartEntry(offer, cartEntries)}
        setSelectedOfferPrice={offers.setSelectedOfferPrice}
        {...locationContext}
        currentCartEntryId={cartIdEditing}
        getCurrentCartEntry={getCurrentCartEntry}
        upsertCart={upsertCart}
        removeFromCart={removeFromCart}
        {...props}
      />
      <SwitchCartMethodDialog />
    </>
  );
};
